/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState, Redirect } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "@/components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "@/examples/Sidenav";
import Configurator from "@/examples/Configurator";

// Material Dashboard 2 React themes
import theme from "@/assets/theme";
import themeRTL from "@/assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "@/assets/theme-dark";
import themeDarkRTL from "@/assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "@/routes";
import routes2 from "@/routes2";

// Material Dashboard 2 React contexts
import { setMiniSidenav, setOpenConfigurator, useMaterialUIController } from "@/context";

// Images
//import brandWhite from "@/assets/images/logo-ct.png";
//import brandDark from "@/assets/images/logo-ct-dark.png";
import brandWhite from "@/assets/images/eurokontrola-za-kontrolu-kvalitete-react-gray.png";
import brandDark from "@/assets/images/eurokontrola-za-kontrolu-kvalitete-react-gray-dark.png";

import { ProtectedLayout } from "@/components/ProtectedLayout";
import { HomeLayout } from "@/components/HomeLayout";

export default function App() {
  const APP_AUTH_SLOT = "EK_APP";
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // console.log("Home");

  // useEffect(() => {
  //   // Fetch the user email and token from local storage
  //   // const user = localStorage.getItem(APP_AUTH_SLOT, false);
  //   const user = localStorage.getItem(APP_AUTH_SLOT) !== null;
  //
  //   console.log("User", user);
  //   // If the token/email does not exist, mark the user as logged out
  //   // if (!user || !user.token) {
  //   if (!user) {
  //     setLoggedIn(false);
  //     return;
  //   }
  //   setLoggedIn(true);
  // }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // console.log(loggedIn);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = <MDBox />;

  const configsButton2 = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              // brandName="Material Dashboard 2"
              brandName="Eurokontrola"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          {/*<Route path="*" element={<Navigate to="/dashboard" />} />*/}
          <Route path="*" element={<Navigate to={pathname + "status"} />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            // brandName="Material Dashboard 2"
            brandName="Eurokontrola"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {localStorage.getItem(APP_AUTH_SLOT) !== null} ? {getRoutes(routes2)}
        <Route path="*" element={<Navigate to={"/authentication/sign-in"} />} /> :
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to={"/status"} />} />
      </Routes>
    </ThemeProvider>
  );
}
